import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock, CheckCircle2 } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brandAssets";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token — use the link from your email");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password-with-token", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — please sign in");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset password");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8">
          <img src={BRAND_ASSETS.logoHorizontalDark} alt="KhojByte" className="h-8" />
        </Link>
        <div className="kb-card p-7">
          {done ? (
            <div className="text-center py-2" data-testid="reset-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" strokeWidth={1.5} />
              <h2 className="font-serif text-xl text-kb-ink mt-4">Password updated</h2>
              <p className="text-sm text-kb-ink-muted mt-2">Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple">SET NEW PASSWORD</div>
              <h1 className="font-serif text-2xl text-kb-ink mt-1.5">Choose a new password</h1>
              <p className="text-sm text-kb-ink-muted mt-3">At least 6 characters. Use something memorable but unique to KhojByte.</p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">New password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
                  <input type="password" required minLength={6} value={pw}
                    onChange={(e) => setPw(e.target.value)} className="kb-input pl-9"
                    placeholder="At least 6 characters" data-testid="reset-pw-input" />
                </div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Confirm password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
                  <input type="password" required minLength={6} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} className="kb-input pl-9"
                    placeholder="Re-type new password" data-testid="reset-confirm-input" />
                </div>
                <button type="submit" disabled={submitting || !token} className="kb-btn-primary w-full justify-center disabled:opacity-50" data-testid="reset-submit-btn">
                  {submitting ? "Saving…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
